import {
  COMPLETE_APP_ONBOARDING_STEP,
  COMPLETE_APP_ONBOARDING_STEP_FAILURE,
  COMPLETE_APP_ONBOARDING_STEP_SUCCESS,
  SKIP_APP_ONBOARDING_STEP,
  SKIP_APP_ONBOARDING_STEP_FAILURE,
  SKIP_APP_ONBOARDING_STEP_SUCCESS,
  FETCH_APP_ONBOARDING,
  FETCH_APP_ONBOARDING_FAILURE,
  FETCH_APP_ONBOARDING_SUCCESS,
  POLL_APP_ONBOARDING_PROGRESS,
  POLL_APP_ONBOARDING_PROGRESS_SUCCESS,
  POLL_APP_ONBOARDING_PROGRESS_FAILURE,
  SHOW_APP_ONBOARDING_COMPLETION_MODAL,
  HIDE_APP_ONBOARDING_COMPLETION_MODAL,
  UPDATE_DISCOUNT_MODAL_SHOWN,
  UPDATE_DISCOUNT_USED_STATUS,
  UPDATE_DISCOUNT_USED_STATUS_SUCCESS,
  UPDATE_DISCOUNT_USED_STATUS_FAILURE,
  FETCH_ONBOARDING_FEATURES,
  FETCH_ONBOARDING_FEATURES_SUCCESS,
  FETCH_ONBOARDING_FEATURES_FAILURE,
  DISMISS_ONBOARDING_FEATURE,
  DISMISS_ONBOARDING_FEATURE_SUCCESS,
  DISMISS_ONBOARDING_FEATURE_FAILURE,
} from "../../modules/constants";

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState = {
  /** Array of step objects returned by the API: { id, key, completed } */
  steps: [],
  /**
   * Trial / credit config from the API:
   * { durationHours, creditAmount, creditCurrency, trialStartedAt }
   */
  trialConfig: null,
  isLoading: false,
  isSubmitting: false,
  error: null,
  /** Whether the completion modal is visible */
  isCompletionModalOpen: false,
  /** Whether the user has already seen the completion modal via API confirmation */
  discountModalShown: false,
  /** Whether the backend considers all steps complete */
  isCompleted: false,
  /** Whether the user earned a discount/credit by completing all steps in time */
  discountEarned: false,
  discountCode: null,
  paddleDiscountId: null,
  discountExpiresAt: null,
  discountUsed: false,
  isUpdatingDiscountUsed: false,
  discountUsedUpdateError: null,
  /** Feature status flags from /api/onboarding/features/ */
  features: {
    has_customer: false,
    has_job: false,
    has_schedule: false,
    has_invoice: false,
    has_team_member: false,
    has_route_optimization: false,
    has_attendance: false,
    has_timesheet: false,
    has_estimate: false,
    has_payment: false,
    dismissed_videos: [],
  },
  /** Video data to show */
  videosToShow: [],
  /** Loading state for features API call */
  isFeaturesLoading: false,
  /** Error from features API call */
  featuresFetchError: null,
  /** Loading state for dismiss API call */
  dismissFeatureLoading: false,
  /** Error from dismiss API call */
  dismissFeatureError: null,
};

// ---------------------------------------------------------------------------
// Individual action handlers
// ---------------------------------------------------------------------------

const handleFetchAppOnboarding = state => ({
  ...state,
  // Only set isLoading: true if there are no steps yet (initial load)
  // Subsequent fetches (silent refetch) won't trigger the global loader
  isLoading: state.steps.length === 0,
  error: null,
});

const handlePollAppOnboardingProgress = state => ({
  ...state,
  error: null,
});

const handleFetchAppOnboardingSuccess = (state, action) => ({
  ...state,
  isLoading: false,
  steps: action.payload.steps,
  trialConfig: action.payload.trialConfig,
  discountModalShown: action.payload.discountModalShown,
  isCompleted: action.payload.isCompleted,
  discountEarned: action.payload.discountEarned,
  discountCode: action.payload.discountCode,
  paddleDiscountId: action.payload.paddleDiscountId,
  discountExpiresAt: action.payload.discountExpiresAt,
  discountUsed: action.payload.discountUsed,
});

const handlePollAppOnboardingProgressSuccess = (state, action) => ({
  ...state,
  steps: action.payload.steps,
  trialConfig: action.payload.trialConfig,
  discountModalShown: action.payload.discountModalShown,
  isCompleted: action.payload.isCompleted,
  discountEarned: action.payload.discountEarned,
  discountCode: action.payload.discountCode,
  paddleDiscountId: action.payload.paddleDiscountId,
  discountExpiresAt: action.payload.discountExpiresAt,
  discountUsed: action.payload.discountUsed,
});

const handlePollAppOnboardingProgressFailure = (state, action) => ({
  ...state,
  error: action.payload,
});

const handleUpdateDiscountUsedStatus = state => ({
  ...state,
  isUpdatingDiscountUsed: true,
  discountUsedUpdateError: null,
});

const handleUpdateDiscountUsedStatusSuccess = (state, action) => ({
  ...state,
  isUpdatingDiscountUsed: false,
  discountUsed: action.payload.discountUsed,
});

const handleUpdateDiscountUsedStatusFailure = (state, action) => ({
  ...state,
  isUpdatingDiscountUsed: false,
  discountUsedUpdateError: action.payload,
});

const handleFetchAppOnboardingFailure = (state, action) => ({
  ...state,
  isLoading: false,
  error: action.payload,
});

const handleCompleteAppOnboardingStep = (state, action) => ({
  ...state,
  isSubmitting: true,
  error: null,
  steps: state.steps.map(s =>
    s.name === action.payload.stepId ? { ...s, status: "completed" } : s
  ),
});

const handleSkipAppOnboardingStep = (state, action) => ({
  ...state,
  error: null,
  // Optimistically mark as skipped to advance UI instantly
  steps: action.payload?.stepId
    ? state.steps.map(s =>
        s.name === action.payload.stepId ? { ...s, status: "skipped" } : s
      )
    : state.steps,
});

const handleCompleteAppOnboardingStepSuccess = (state, action) => ({
  ...state,
  isSubmitting: false,
  // Locally mark the step as completed so no full refetch is needed
  steps: action.payload?.stepId
    ? state.steps.map(s =>
        s.name === action.payload.stepId || s.id === action.payload.stepId ? { ...s, status: "completed" } : s
      )
    : state.steps,
});

const handleSkipAppOnboardingStepSuccess = (state, action) => ({
  ...state,
  // Confirm the skip (already marked by SKIP_APP_ONBOARDING_STEP)
  steps: action.payload?.stepId
    ? state.steps.map(s =>
        s.name === action.payload.stepId ? { ...s, status: "skipped" } : s
      )
    : state.steps,
});

const handleCompleteAppOnboardingStepFailure = (state, action) => ({
  ...state,
  isSubmitting: false,
  error: action.payload,
});

const handleSkipAppOnboardingStepFailure = (state, action) => ({
  ...state,
  error: action.payload?.error,
  // Rollback to null/pending on failure
  steps: action.payload?.stepId
    ? state.steps.map(s =>
        s.name === action.payload.stepId ? { ...s, status: null } : s
      )
    : state.steps,
});


const handleShowCompletionModal = state => ({
  ...state,
  isCompletionModalOpen: true,
});

const handleHideCompletionModal = state => ({
  ...state,
  isCompletionModalOpen: false,
});

const handleUpdateDiscountModalShown = state => ({
  ...state,
  discountModalShown: true,
});

const handleFetchOnboardingFeatures = state => ({
  ...state,
  isFeaturesLoading: true,
  featuresFetchError: null,
});

const handleFetchOnboardingFeaturesSuccess = (state, action) => ({
  ...state,
  isFeaturesLoading: false,
  features: action.payload.features,
  videosToShow: action.payload.videosToShow || [],
});

const handleFetchOnboardingFeaturesFailure = (state, action) => ({
  ...state,
  isFeaturesLoading: false,
  featuresFetchError: action.payload,
});

const handleDismissFeature = state => {
  console.log("handleDismissFeature: setting dismissFeatureLoading to true");
  return {
    ...state,
    dismissFeatureLoading: true,
    dismissFeatureError: null,
  };
};

const handleDismissFeatureSuccess = (state, action) => {
  console.log("handleDismissFeatureSuccess: updating dismissed_videos", action.payload);
  const existingDismissed = state.features?.dismissed_videos || [];
  // Use API response if available, otherwise append featureName locally
  const updatedDismissed = Array.isArray(action.payload.dismissed_videos)
    ? action.payload.dismissed_videos
    : [...existingDismissed, action.payload.featureName];
  return {
    ...state,
    dismissFeatureLoading: false,
    features: {
      ...state.features,
      dismissed_videos: updatedDismissed,
    },
  };
};

const handleDismissFeatureFailure = (state, action) => ({
  ...state,
  dismissFeatureLoading: false,
  dismissFeatureError: action.payload,
});

// ---------------------------------------------------------------------------
// Action-handler lookup table
// ---------------------------------------------------------------------------

const ACTION_HANDLERS = {
  [FETCH_APP_ONBOARDING]: handleFetchAppOnboarding,
  [FETCH_APP_ONBOARDING_SUCCESS]: handleFetchAppOnboardingSuccess,
  [FETCH_APP_ONBOARDING_FAILURE]: handleFetchAppOnboardingFailure,
  [COMPLETE_APP_ONBOARDING_STEP]: handleCompleteAppOnboardingStep,
  [COMPLETE_APP_ONBOARDING_STEP_SUCCESS]: handleCompleteAppOnboardingStepSuccess,
  [COMPLETE_APP_ONBOARDING_STEP_FAILURE]: handleCompleteAppOnboardingStepFailure,
  [SKIP_APP_ONBOARDING_STEP]: handleSkipAppOnboardingStep,
  [SKIP_APP_ONBOARDING_STEP_SUCCESS]: handleSkipAppOnboardingStepSuccess,
  [SKIP_APP_ONBOARDING_STEP_FAILURE]: handleSkipAppOnboardingStepFailure,

  [SHOW_APP_ONBOARDING_COMPLETION_MODAL]: handleShowCompletionModal,
  [HIDE_APP_ONBOARDING_COMPLETION_MODAL]: handleHideCompletionModal,
  [UPDATE_DISCOUNT_MODAL_SHOWN]: handleUpdateDiscountModalShown,
  [UPDATE_DISCOUNT_USED_STATUS]: handleUpdateDiscountUsedStatus,
  [UPDATE_DISCOUNT_USED_STATUS_SUCCESS]: handleUpdateDiscountUsedStatusSuccess,
  [UPDATE_DISCOUNT_USED_STATUS_FAILURE]: handleUpdateDiscountUsedStatusFailure,
  [FETCH_ONBOARDING_FEATURES]: handleFetchOnboardingFeatures,
  [FETCH_ONBOARDING_FEATURES_SUCCESS]: handleFetchOnboardingFeaturesSuccess,
  [FETCH_ONBOARDING_FEATURES_FAILURE]: handleFetchOnboardingFeaturesFailure,
  [POLL_APP_ONBOARDING_PROGRESS]: handlePollAppOnboardingProgress,
  [POLL_APP_ONBOARDING_PROGRESS_SUCCESS]: handlePollAppOnboardingProgressSuccess,
  [POLL_APP_ONBOARDING_PROGRESS_FAILURE]: handlePollAppOnboardingProgressFailure,
  [DISMISS_ONBOARDING_FEATURE]: handleDismissFeature,
  [DISMISS_ONBOARDING_FEATURE_SUCCESS]: handleDismissFeatureSuccess,
  [DISMISS_ONBOARDING_FEATURE_FAILURE]: handleDismissFeatureFailure,
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export default function appOnboardingReducer(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
